import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Title, Colors, Body as Tag, Button } from '@cognite/cogs.js';
import { Icon } from 'components/Icon';
import { useDebounce } from 'hooks/useDebounce';
import { Cascader, Row } from 'antd';
import { DefaultOptionType } from 'antd/lib/cascader';
import { mapPropertiesToAttributes } from 'utils/project-ruleset';
import { Schema } from 'hooks/projects';
import { PRIMARY_KEY_FDM } from 'utils/config';

export const SchemaAttributesSelection = ({
  selectedSchemaId,
  currentSchema,
  schemas,
  attributesList,
  setSelectedSchemaId,
  onSchemaAttributesChange,
}: {
  selectedSchemaId: string;
  currentSchema?: Schema | null;
  schemas?: Schema[];
  attributesList: string[];
  setSelectedSchemaId: React.Dispatch<React.SetStateAction<string>>;
  onSchemaAttributesChange: (schemaType: string, attributes: string[]) => void;
}) => {
  const [searchAttribute, setSearchAttribute] = useState('');
  const debouncedSearchAttribute = useDebounce(searchAttribute, 75);

  const primaryKeyAttribute = PRIMARY_KEY_FDM;
  const schemaName = currentSchema?.name;

  if (!schemaName) {
    return <span>Couldn't load schema type</span>;
  }

  const getAttributesOptions = () => {
    if (!currentSchema || !schemas) return [];

    // Many to many disabled until further notice
    const mappedAttributes = mapPropertiesToAttributes(
      currentSchema,
      schemas,
      true
    );
    // Filter out the primary key attribute
    const attributesWithoutPrimaryKey = mappedAttributes.filter(
      (att) => att.value !== primaryKeyAttribute
    );

    // Filter out the already selected attributes
    const filteredAttributesOptions = attributesWithoutPrimaryKey?.filter(
      (att) => !attributesList.some((selectedAtt) => selectedAtt === att.value)
    );
    return filteredAttributesOptions;
  };

  const attributesOptions = getAttributesOptions();

  const onHandleSelectedAttribute = (
    selectedOptions: DefaultOptionType[] | undefined
  ) => {
    const value = selectedOptions?.map((opt) => opt.value).join('.') || '';
    onSchemaAttributesChange(schemaName, [...attributesList, value]);
  };

  const onHandleRemoveAttribute = (attributeToRemove: string) => {
    const updatedAttributes = attributesList.filter(
      (att) => att !== attributeToRemove
    );
    onSchemaAttributesChange(schemaName, updatedAttributes);
  };

  const searchAttributeFilter = (
    inputValue: string,
    path: DefaultOptionType[]
  ) =>
    debouncedSearchAttribute === searchAttribute && // do not filter until debounce has set in
    !path[path.length - 1].disabled && // remove disabled options
    path.some(
      (option) =>
        option.label &&
        option?.label
          ?.toString()
          ?.toLowerCase()
          .indexOf(debouncedSearchAttribute.toLowerCase()) > -1
    );

  const renderAttributesCascader = () => (
    <>
      <Title level={6}>{schemaName}</Title>
      <Row align="middle">
        <StyledCascaderContainer>
          <Cascader
            multiple={false}
            onSearch={(inputValue) => {
              setSearchAttribute(inputValue);
            }}
            searchValue={searchAttribute}
            options={
              attributesOptions || [{ label: 'Select attributes…', value: '' }]
            }
            onChange={(_val, selectedOptions): void => {
              setSearchAttribute('');
              onHandleSelectedAttribute(selectedOptions);
            }}
            allowClear={false}
            suffixIcon={
              <Icon
                type={
                  selectedSchemaId === currentSchema?.id
                    ? 'ChevronUp'
                    : 'ChevronDown'
                }
              />
            }
            value={['Select an attribute']}
            showSearch={{
              limit: 2000,
              matchInputWidth: false,
              filter: searchAttributeFilter,
            }}
            popupVisible={selectedSchemaId === currentSchema?.id}
            onPopupVisibleChange={(isVisible) =>
              setSelectedSchemaId(isVisible ? (currentSchema?.id ?? '') : '')
            }
            notFoundContent={
              debouncedSearchAttribute !== searchAttribute
                ? 'Loading …'
                : 'No attribute found'
            }
            disabled={!attributesOptions?.length}
            expandIcon={<Icon type="ChevronRightSmall" />}
            getPopupContainer={(node) => node}
            displayRender={(label) => label.join('.')}
            placeholder="Select an attribute…"
          />
        </StyledCascaderContainer>
        {attributesList.length > 0 && (
          <StyledRemoveButton
            size="small"
            icon="Delete"
            type="ghost"
            aria-label={`Remove all attributes for schema ${schemaName}`}
            onClick={() => onSchemaAttributesChange(schemaName, [])}
          />
        )}
      </Row>
    </>
  );

  const renderSelectedAttributes = () => (
    <StyledRow>
      <StyledLabel>{primaryKeyAttribute}</StyledLabel>
      {attributesList.map((attribute) => (
        <StyledLabel>
          {attribute}
          <Icon
            type="Close"
            size={12}
            onClick={() => onHandleRemoveAttribute(attribute)}
            style={{ verticalAlign: 'middle', marginLeft: '0.4rem' }}
          />
        </StyledLabel>
      ))}
    </StyledRow>
  );

  return (
    <StyledContainer>
      {renderAttributesCascader()}
      {renderSelectedAttributes()}
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  margin-bottom: 1rem;
  border-radius: 5px;
  .cogs-title-6 {
    margin: 0.2rem;
  }
`;

const StyledRow = styled(Row)`
  margin: 0.2rem 0;
  overflow: auto;
  max-height: 15vh;
  span {
    margin-left: 0.3rem;
  }
`;

const StyledCascaderContainer = styled.div`
  margin-right: 1rem;
  .ant-cascader {
    display: flex;
    .ant-select-selector {
      height: 36px;
      width: 200px;
      background: white;
      border-radius: var(--cogs-border-radius--default);
      border: 2px solid ${Colors['decorative--grayscale--400']};
      transition: border var(--cogs-transition-time-fast) linear;
      &:focus-within {
        border: 2px solid var(--cogs-primary);
        outline: none;
      }
      input {
        border: 0;
        outline: none;
      }
    }
    &.ant-select-disabled {
      .ant-select-arrow {
        color: var(--cogs-bg-control--disabled);
      }
    }
    .ant-select-arrow {
      transform: translateY(-50%);
      margin-top: 0;
      width: 16px;
      height: 16px;
      color: ${Colors['decorative--grayscale--600']};
    }
  }
  .ant-select-dropdown {
    background: ${Colors['decorative--grayscale--white']};
    border-radius: var(--cogs-border-radius--default);
    box-shadow: var(--cogs-z-8);
  }
  .ant-cascader-menu-item-active {
    background: var(--cogs-bg-selected);
  }
  .ant-cascader-menu-item-keyword {
    color: var(--cogs-text-accent);
  }
  .ant-cascader-menu-item-expand {
    display: flex;
    flex: 1;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
    padding: 5px 5px 5px 12px;
  }
  .ant-cascader-menu-item-expand-icon {
    right: 0;
    padding-left: 10px;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    .cogs-icon {
      color: var(--cogs-text-primary);
    }
  }
`;

const StyledLabel = styled(Tag)`
  background: var(--cogs-bg-control--secondary) !important;
  margin: 0.2rem;
  /* Replaced Colors token with CSS var for COGS v9 */
  border: 1px solid var(--cogs-text-secondary);
  box-shadow: none !important;
  border-radius: 5px;
  height: 28px;
  font-size: 12px;
  line-height: 28px;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
`;

const StyledRemoveButton = styled(Button)`
  transition: all 250ms linear;
  &:hover {
    ::after {
      content: 'Reset attributes';
    }
  }
`;
